import { useEffect, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { KPIsWidgetEnum, LongTitles } from '../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { CaseTypeEnum } from 'api/model';
import useTranslations from 'contexts/basicData/useTranslations';
import useCustomInstance from 'api/mutator/custom-instance';
import { useMutation } from '@tanstack/react-query';
import Button from 'components/inputs/Button';
import { showException } from 'utils/exception-helper';
import { downloadBlob } from 'utils/file-utils';
import { formatDate } from 'utils/date-helpers';

interface AverageDrivingDistanceDataPoint {
  caseType: CaseTypeEnum;
  countyID: number | null;
  countyName: string;
  totalCases: number;
  averageActualKm: number;
  averageEstimatedKm: number;
  averageActualKmForEstimatedSubset: number;
  totalCasesWithEstimate: number;
  totalActualKm: number;
}

interface AverageDrivingDistanceResult {
  dataPoints: AverageDrivingDistanceDataPoint[];
}

interface AverageDrivingDistanceRequest {
  startDate: Date;
  endDate: Date;
  caseTypes?: number[];
  assignmentTypes?: number[];
  supplierIDs?: number[];
  customerNames?: string[];
  fromCountyIDs?: number[];
  toCountyIDs?: number[];
}

type SortField =
  | 'caseType'
  | 'county'
  | 'totalCases'
  | 'averageActualKm'
  | 'averageEstimatedKm';
type SortDirection = 'asc' | 'desc';

const SortIndicator = ({
  field,
  sortField,
  sortDirection,
}: {
  field: SortField;
  sortField: SortField;
  sortDirection: SortDirection;
}) => {
  if (sortField !== field) return null;
  return <span>{sortDirection === 'asc' ? ' ▲' : ' ▼'}</span>;
};

const formatKm = (km: number) =>
  km.toLocaleString('sv-SE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

const AverageDrivingDistanceWidget = () => {
  const [sortField, setSortField] = useState<SortField>('totalCases');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [isDownloading, setIsDownloading] = useState(false);

  const { caseTypes } = useTranslations();

  const {
    dashboardState: {
      daterange,
      selectedCaseTypes,
      selectedAssignmentTypes,
      selectedSupplierIDs,
      selectedCustomerNames,
      selectedFromCountyIDs,
      selectedToCountyIDs,
    },
  } = useDashboardControls();

  const customInstance = useCustomInstance<AverageDrivingDistanceResult>();
  const getData = useMutation({
    mutationFn: (data: AverageDrivingDistanceRequest) =>
      customInstance({
        url: '/api/KPI/GetAverageDrivingDistanceByCaseTypeAndCounty',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data,
      }),
  });

  const downloadExcel = useCustomInstance<Blob>();

  const startDate = daterange[0];
  const endDate = daterange[1];

  const buildRequest = (): AverageDrivingDistanceRequest | null => {
    if (!startDate || !endDate) {
      return null;
    }
    return {
      startDate,
      endDate,
      caseTypes: selectedCaseTypes,
      assignmentTypes: selectedAssignmentTypes,
      supplierIDs: selectedSupplierIDs,
      customerNames: selectedCustomerNames,
      fromCountyIDs: selectedFromCountyIDs,
      toCountyIDs: selectedToCountyIDs,
    };
  };
  const startDateTime = startDate ? startDate.getTime() : null;
  const endDateTime = endDate ? endDate.getTime() : null;

  useEffect(() => {
    const request = buildRequest();
    if (request) {
      getData.mutate(request);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDateTime,
    endDateTime,
    selectedCaseTypes,
    selectedAssignmentTypes,
    selectedSupplierIDs,
    selectedCustomerNames,
    selectedFromCountyIDs,
    selectedToCountyIDs,
  ]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortData = (
    data: AverageDrivingDistanceDataPoint[]
  ): AverageDrivingDistanceDataPoint[] => {
    return [...data].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortField) {
        case 'caseType':
          aVal = caseTypes[a.caseType] || '';
          bVal = caseTypes[b.caseType] || '';
          break;
        case 'county':
          aVal = a.countyName || '';
          bVal = b.countyName || '';
          break;
        case 'totalCases':
          aVal = a.totalCases;
          bVal = b.totalCases;
          break;
        case 'averageActualKm':
          aVal = a.averageActualKm;
          bVal = b.averageActualKm;
          break;
        case 'averageEstimatedKm':
          aVal = a.averageEstimatedKm;
          bVal = b.averageEstimatedKm;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string') {
        const comparison = aVal.localeCompare(bVal as string);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      return sortDirection === 'asc'
        ? aVal - (bVal as number)
        : (bVal as number) - aVal;
    });
  };

  const currentData = getData.data?.dataPoints || [];
  const sortedData = sortData(currentData);
  const totalCases = currentData.reduce((sum, dp) => sum + dp.totalCases, 0);

  const headerCellStyle = (align: 'left' | 'right') => ({
    cursor: 'pointer' as const,
    padding: '8px',
    textAlign: align,
    borderBottom: '2px solid #ddd',
  });

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.AverageDrivingDistance]}
      count={totalCases}
      isLoading={getData.isLoading}
      info="Visar snittsträcka (estimerad vs faktiskt utfall) per ärendetyp och län, aggregerat per ärende. Länet är ärendets första startlän, utom för hemleverans där det är sista slutlänet."
    >
      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
        <Button
          disabled={isDownloading || !startDate || !endDate}
          onClick={async () => {
            const request = buildRequest();
            if (!request) return;
            try {
              setIsDownloading(true);
              const result = await downloadExcel({
                url: '/api/Export/ExportAverageDrivingDistanceByCaseTypeAndCounty',
                method: 'POST',
                responseType: 'blob',
                headers: { 'Content-Type': 'application/json' },
                data: request,
              });
              if (result) {
                downloadBlob(
                  result,
                  `Nyckeltal_SnittstrackaVsUtfall_${formatDate(startDate!)}_${formatDate(endDate!)}.xlsx`
                );
              }
            } catch (error) {
              showException(error);
            } finally {
              setIsDownloading(false);
            }
          }}
        >
          <FontAwesomeIcon icon={faDownload} /> Ladda ner
          {isDownloading ? (
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              style={{ marginLeft: '5px' }}
            />
          ) : null}
        </Button>
      </div>

      {sortedData.length === 0 ? (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          {getData.isLoading ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <p>Ingen data att visa f&ouml;r vald filtrering.</p>
          )}
        </div>
      ) : (
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead
              style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}
            >
              <tr>
                <th
                  onClick={() => handleSort('caseType')}
                  style={headerCellStyle('left')}
                >
                  &Auml;rendetyp
                  <SortIndicator
                    field="caseType"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('county')}
                  style={headerCellStyle('left')}
                >
                  L&auml;n
                  <SortIndicator
                    field="county"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('totalCases')}
                  style={headerCellStyle('right')}
                >
                  Antal
                  <SortIndicator
                    field="totalCases"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('averageEstimatedKm')}
                  style={headerCellStyle('right')}
                >
                  Snitt estimerad (km)
                  <SortIndicator
                    field="averageEstimatedKm"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('averageActualKm')}
                  style={headerCellStyle('right')}
                >
                  Snitt faktisk (km)
                  <SortIndicator
                    field="averageActualKm"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  style={{
                    padding: '8px',
                    textAlign: 'right',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  Diff %
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((dp) => {
                const hasEstimate = dp.totalCasesWithEstimate > 0;
                // Compare actual vs estimated on the same subset (cases that have a full estimate)
                const diffPct =
                  hasEstimate && dp.averageEstimatedKm > 0
                    ? ((dp.averageActualKmForEstimatedSubset -
                        dp.averageEstimatedKm) /
                        dp.averageEstimatedKm) *
                      100
                    : null;
                const diffColor =
                  diffPct !== null && Math.abs(diffPct) > 15
                    ? '#c0392b'
                    : undefined;
                return (
                  <tr
                    key={`${dp.caseType}-${dp.countyID ?? 'none'}`}
                    style={{ borderBottom: '1px solid #eee' }}
                  >
                    <td style={{ padding: '8px' }}>
                      {caseTypes[dp.caseType] || '(Okänd)'}
                    </td>
                    <td style={{ padding: '8px' }}>
                      {dp.countyName || '(Okänt län)'}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      {dp.totalCases}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      {hasEstimate ? formatKm(dp.averageEstimatedKm) : '–'}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      {formatKm(dp.averageActualKm)}
                    </td>
                    <td
                      style={{
                        padding: '8px',
                        textAlign: 'right',
                        color: diffColor,
                      }}
                    >
                      {diffPct !== null
                        ? `${diffPct > 0 ? '+' : ''}${diffPct.toFixed(1)}%`
                        : '–'}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: '2px solid #ddd', fontWeight: 'bold' }}>
                <td style={{ padding: '8px' }}>Totalt</td>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <td aria-label="empty">&nbsp;</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>
                  {totalCases}
                </td>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <td colSpan={3} aria-label="empty">
                  &nbsp;
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Widget>
  );
};

export default AverageDrivingDistanceWidget;
